import { call, GRID_STALE_TIME, type Market } from "./api";
import type { FeedIssue } from "./feed_issues";

/** One closed calendar month's Shopify actuals, with every money and percentage figure already computed by C2. */
export type SkuMonthRow = {
  month: string;
  orders: number;
  orders_total: number;
  units: number;
  revenue: string | null;
  refunds: string | null;
  cogs: string | null;
  fees: string | null;
  other: string | null;
  other_state: "fed" | "not_fed";
  profit: string | null;
  profit_per_unit: string | null;
  margin_pct: string | null;
  missing: string[];
};

/** C2 v1.55's three-month actuals answer for one Shopify SKU. */
export type SkuMonths = {
  xero_sku: string;
  product: string;
  variant: string;
  channel_location: string;
  market: Market;
  currency: string;
  months: SkuMonthRow[];
  window: { from: string; to: string };
  basis: string;
  method: string;
  /** C2 v1.62: the open issues of the order feeds behind these months; absent from an older API. */
  feed_issues?: FeedIssue[];
};

export const getSkuMonths = (sku: string) => call<SkuMonths>(`/sku-months/${encodeURIComponent(sku)}`);

/** One cached C2 read per SKU, on the same five-minute policy as the Price Run grid. */
export function skuMonthsQuery(sku: string) {
  return {
    queryKey: ["pricing", "sku-months", sku] as const,
    queryFn: () => getSkuMonths(sku),
    retry: 1,
    staleTime: GRID_STALE_TIME,
  };
}

export const SKU_MONTHS_COPY = {
  title: "Last 3 months",
  intro: "What this SKU actually earned per month, from the Shopify order book: units sold, revenue less refunds, COGS at the order date, billed fulfilment fees and PFC's monthly other costs.",
  head: ["Month", "Units", "Revenue", "Net profit", "Per unit", "Margin"] as const,
  unavailable: "Monthly actuals not yet available",
  coverage: (parts: string[]) => `Costed orders: ${parts.join(" · ")}`,
  coveragePart: (month: string, costed: number, total: number) => `${month} ${costed} of ${total}`,
} as const;

const MONTH_GAP: Record<string, string> = {
  no_orders: "no orders",
  no_costed_orders: "orders exist, none costed yet",
  no_other_cost_month: "other costs not yet closed for this month, profit before them",
};

export function monthGapText(reason: string): string {
  return MONTH_GAP[reason] ?? reason;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** A fixed English month label; Intl spells September as "Sept" in en-GB. */
export function monthLabel(iso: string): string {
  const [year, month] = iso.split("-");
  return `${MONTHS[Number(month) - 1] ?? month} ${year}`;
}
